import './editor.css';
import { EditorStore } from './editor-store';
import { EditorPreview } from './editor-preview';
import { EditorOutline } from './editor-outline';
import { EditorInspector } from './editor-inspector';
import { History } from './editor-history';
import { EditorToolbar, type PaletteTool } from './editor-toolbar';
import { addObstacle, addCollectible, addTrigger } from './editor-commands';
import { pasteObstacle } from './editor-clipboard';
import { replaceColor } from './editor-colors';
import type { LevelTrigger } from '@/game/level';
import { hitTest } from './editor-selection';
import { showAlert, showConfirm, showChoice, showTwoInputs } from './editor-dialogs';
import {
  serializePack, parseImport, planConflicts, applyConflictPlan,
  type Resolution,
} from './editor-pack';
import { EditorStorage } from './editor-storage';
import { maybeShowTour } from './editor-tour';
import { validate } from './editor-validation';
import type { LevelData } from '@/game/level';
import type { StorageManager } from '@/storage/save-data';
import type { SyncManager } from '@/sync/sync-manager';

export interface EditorScreenOptions {
  storage: StorageManager;
  syncManager?: SyncManager;
  onExit: () => void;
  onTestRequest: (data: LevelData, startOffset: number, onFinish: (exitScroll: number) => void) => void;
  resolveBuiltin?: (id: string) => LevelData | undefined;
}

export class EditorScreen {
  private root: HTMLDivElement;
  private outlineEl: HTMLDivElement;
  private previewEl: HTMLDivElement;
  private inspectorEl: HTMLDivElement;
  private toolbarEl: HTMLDivElement;
  private fabEl: HTMLDivElement;

  private readonly opts: EditorScreenOptions;
  private editorStorage: EditorStorage;
  private resizeObserver: ResizeObserver | null = null;
  private store: EditorStore | null = null;
  private editorPreview: EditorPreview | null = null;
  private outlinePane: EditorOutline | null = null;
  private inspectorPane: EditorInspector | null = null;
  private toolbarCtrl: EditorToolbar | null = null;
  private history = new History();
  private beforeUnloadHandler = () => this.editorStorage.flush();

  /** Called when the user taps a sync action button in the toolbar. */
  onSyncRequest: ((action: 'push' | 'pull' | 'resolve') => Promise<void>) | null = null;

  constructor(opts: EditorScreenOptions) {
    this.opts = opts;
    this.editorStorage = new EditorStorage(opts.storage);
    this.root = document.createElement('div');
    this.root.className = 'editor-root';
    this.root.style.display = 'none';
    this.root.setAttribute('role', 'application');
    this.root.setAttribute('aria-label', 'Level editor');

    this.toolbarEl = document.createElement('div');
    this.toolbarEl.className = 'editor-toolbar';
    this.toolbarEl.setAttribute('role', 'toolbar');
    this.toolbarEl.setAttribute('aria-label', 'Editor toolbar');
    this.root.appendChild(this.toolbarEl);

    const body = document.createElement('div');
    body.className = 'editor-body';
    this.root.appendChild(body);

    this.outlineEl = document.createElement('div');
    this.outlineEl.className = 'editor-outline';
    this.outlineEl.innerHTML = '<div class="editor-empty">Outline (empty)</div>';
    this.outlineEl.setAttribute('role', 'navigation');
    this.outlineEl.setAttribute('aria-label', 'Level outline');
    body.appendChild(this.outlineEl);

    this.previewEl = document.createElement('div');
    this.previewEl.className = 'editor-preview';
    this.previewEl.innerHTML = '<div class="editor-empty">Preview (shell only)</div>';
    this.previewEl.setAttribute('role', 'region');
    this.previewEl.setAttribute('aria-label', 'Level preview');
    body.appendChild(this.previewEl);

    this.inspectorEl = document.createElement('div');
    this.inspectorEl.className = 'editor-inspector';
    this.inspectorEl.innerHTML = '<div class="editor-empty">Inspector (empty)</div>';
    this.inspectorEl.setAttribute('role', 'region');
    this.inspectorEl.setAttribute('aria-label', 'Selection inspector');
    body.appendChild(this.inspectorEl);

    this.fabEl = document.createElement('div');
    this.fabEl.className = 'editor-fab';
    this.previewEl.appendChild(this.fabEl);

    this.buildToolbarStub();
    document.body.appendChild(this.root);
    EditorScreen.injectToolbarScrollStyle();
  }

  private static injectToolbarScrollStyle(): void {
    const SENTINEL = 'data-editor-toolbar-scroll';
    if (document.querySelector(`[${SENTINEL}]`)) return;
    const style = document.createElement('style');
    style.setAttribute(SENTINEL, '');
    style.textContent = '.editor-toolbar { overflow-x: auto; overflow-y: hidden; flex-wrap: nowrap; scrollbar-width: thin; scrollbar-color: #00ffff44 transparent; }';
    document.head.appendChild(style);
  }

  private buildToolbarStub(): void {
    const back = document.createElement('button');
    back.textContent = '◀ Back';
    back.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      this.opts.onExit();
    });
    this.toolbarEl.appendChild(back);

    const outlineToggle = document.createElement('button');
    outlineToggle.textContent = '☰ Outline';
    outlineToggle.className = 'outline-toggle';
    outlineToggle.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      this.outlineEl.classList.toggle('open');
    });
    this.toolbarEl.appendChild(outlineToggle);

    const spacer = document.createElement('div');
    spacer.className = 'spacer';
    this.toolbarEl.appendChild(spacer);

    const inspectorToggle = document.createElement('button');
    inspectorToggle.textContent = '⚙ Props';
    inspectorToggle.addEventListener('pointerdown', (e) => {
      e.stopPropagation();
      this.inspectorEl.classList.toggle('open');
    });
    this.toolbarEl.appendChild(inspectorToggle);
  }

  loadLevel(data: LevelData): void {
    this.history.clear();
    this.store = new EditorStore(data);
    this.previewEl.innerHTML = '';
    this.outlineEl.innerHTML = '';
    this.inspectorEl.innerHTML = '';
    this.editorPreview?.destroy();
    this.editorPreview = new EditorPreview(this.previewEl, this.store);
    this.outlinePane = new EditorOutline(this.outlineEl, this.store, this.history);
    this.inspectorPane = new EditorInspector(this.inspectorEl, this.store, this.history);

    // Construct the toolbar on top of our existing static buttons
    this.toolbarCtrl = new EditorToolbar(this.toolbarEl, this.store, this.history, (tool) => {
      this.editorPreview?.setTool(tool as PaletteTool);
    });

    this.toolbarCtrl.onTestRequest = () => {
      if (!this.store || !this.editorPreview) return;
      const lvl = this.store.level;
      const playhead = this.editorPreview.playhead;
      this.hide();
      this.opts.onTestRequest(lvl, playhead, (exitScroll) => {
        this.show();
        this.loadLevel(lvl); // re-hydrate everything
        // Restore playhead to where the player exited/died
        this.editorPreview?.setPlayhead(Math.max(0, exitScroll));
      });
    };

    // Wire preview taps
    this.editorPreview!.onWorldSelect = (wx, wy) => {
      const sel = hitTest(this.store!.level, wx, wy);
      if (sel) this.store!.setSelection(sel);
      else this.store!.setSelection({ kind: 'none' });
    };
    this.editorPreview!.onWorldTap = (wx, wy) => {
      if (!this.toolbarCtrl || !this.store) return;
      const t = this.toolbarCtrl.currentTool;
      if (!t) return;
      const seg = this.store.level.segments[0];
      if (!seg) return;
      if (t === 'spike') {
        this.history.execute(addObstacle(this.store, seg.id, {
          type: 'spike', position: { along: Math.max(0, wx), across: 0, up: 0 },
          size: { width: 20, height: 30 },
        }));
      } else if (t === 'wall') {
        this.history.execute(addObstacle(this.store, seg.id, {
          type: 'wall', position: { along: Math.max(0, wx), across: 0, up: 0 },
          size: { width: 30, height: 40 },
        }));
      } else if (t === 'moving') {
        this.history.execute(addObstacle(this.store, seg.id, {
          type: 'moving',
          position: { along: Math.max(0, wx), across: 0, up: 50 },
          size: { width: 30, height: 30 },
          behavior: { motion: 'sine', amplitude: 40, frequency: 0.5, phase: 0 },
        }));
      } else if (t === 'jumppad-small' || t === 'jumppad-medium' || t === 'jumppad-large') {
        const padSize = t.slice('jumppad-'.length) as 'small' | 'medium' | 'large';
        this.history.execute(addObstacle(this.store, seg.id, {
          type: 'jumppad',
          position: { along: Math.max(0, wx), across: 0, up: 0 },
          size: { width: 0, height: 0 }, // 0 → loader uses tier defaults
          visual: { color: null, glow: 1, padSize },
        }));
      } else if (t === 'orb-small' || t === 'orb-medium' || t === 'orb-large') {
        const orbSize = t.slice('orb-'.length) as 'small' | 'medium' | 'large';
        this.history.execute(addObstacle(this.store, seg.id, {
          type: 'orb',
          // Tap's wy is world-Y. Subtract groundY (80) so the JSON 'up' matches
          // the floating offset above the floor — same convention as stars.
          position: { along: Math.max(0, wx), across: 0, up: Math.max(20, wy - 80) },
          size: { width: 0, height: 0 }, // 0 → loader uses tier default diameter
          visual: { color: null, glow: 1, orbSize },
        }));
      } else if (t === 'star') {
        this.history.execute(addCollectible(this.store, {
          type: 'star', position: { along: wx, across: 0, up: Math.max(0, wy - 80) }, index: 0,
        }));
      } else if (t === 'fragment') {
        this.history.execute(addCollectible(this.store, {
          type: 'fragment', position: { along: wx, across: 0, up: Math.max(0, wy - 80) }, index: 0,
        }));
      } else if (t === 'powerup') {
        this.history.execute(addCollectible(this.store, {
          type: 'powerup', position: { along: wx, across: 0, up: Math.max(0, wy - 80) }, powerupType: 'taylor_expansion',
        }));
      } else if (t === 'paste') {
        const clip = pasteObstacle();
        if (!clip) return;
        const copy = JSON.parse(JSON.stringify(clip));
        copy.position = { ...copy.position, along: Math.max(0, wx) };
        this.history.execute(addObstacle(this.store, seg.id, copy));
      } else if (t && t.startsWith('trigger:')) {
        const triggerType = t.slice('trigger:'.length);
        this.history.execute(addTrigger(this.store, {
          id: `trg-${crypto.randomUUID().slice(0, 6)}`,
          type: triggerType as LevelTrigger['type'],
          position: Math.max(0, wx),
          duration: 0,
          easing: 'linear',
          params: triggerDefaults(triggerType),
        }));
      }
    };

    // Autosave registration
    this.editorStorage.setCurrentLevel(data);
    this.store!.on('level-changed', () => this.editorStorage.noteChange());

    // Save button handler
    this.toolbarCtrl!.onSaveRequest = async () => {
      this.editorStorage.save();
      await showAlert('Saved.');
    };

    this.toolbarCtrl!.onSaveBlockedRequest = async () => {
      const issues = validate(this.store!.level).blocking;
      await showAlert(`Can't save — ${issues.length} issue(s):\n\n${issues.map((i) => '• ' + i.message).join('\n')}`);
    };

    this.toolbarCtrl!.onExportRequest = async () => {
      const lvl = this.store!.level;
      const json = serializePack(
        { name: lvl.meta.name, author: lvl.meta.author ?? '' },
        [lvl],
      );
      await offerDownload(`${slug(lvl.meta.name)}.json`, json);
    };
    this.toolbarCtrl!.onImportRequest = () => this.promptImport();

    this.toolbarCtrl!.onResetRequest = async () => {
      const src = this.opts.resolveBuiltin?.(data.meta.source?.sourceId ?? '');
      if (!src) { await showAlert('Original builtin not found.'); return; }
      if (!(await showConfirm('Reset to original', 'Discard all your edits to this clone and restore the original? Metadata (name, author) is preserved.'))) return;
      const reset = JSON.parse(JSON.stringify(src)) as LevelData;
      reset.meta.id = data.meta.id;
      reset.meta.name = data.meta.name;
      reset.meta.author = data.meta.author;
      reset.meta.source = data.meta.source;
      reset.meta.createdAt = data.meta.createdAt;
      reset.meta.updatedAt = new Date().toISOString();
      this.store!.setLevel(reset);
      this.history.clear();
      this.editorStorage.setCurrentLevel(reset);
    };

    this.toolbarCtrl!.onColorReplaceRequest = async () => {
      const vals = await showTwoInputs(
        'Replace color',
        { label: 'Find (hex)', type: 'color', initial: '#00ffff' },
        { label: 'Replace with (hex)', type: 'color', initial: '#ff00ff' },
      );
      if (!vals) return;
      const [from, to] = vals;
      if (!from || !to) return;
      const before = JSON.parse(JSON.stringify(this.store!.level));
      let n = 0;
      const cmd = {
        kind: 'ReplaceColor', targetKey: `${from}->${to}`,
        do: () => { this.store!.mutate((lvl) => { n = replaceColor(lvl, from, to); }); },
        undo: () => { this.store!.setLevel(before); },
      };
      this.history.execute(cmd);
      await showAlert(`Replaced ${n} occurrence(s).`);
    };

    this.toolbarCtrl!.onSyncRequest = async (action) => {
      if (this.onSyncRequest) {
        await this.onSyncRequest(action);
        void this.refreshSyncState();
      }
    };

    void this.refreshSyncState();
  }

  private async refreshSyncState(): Promise<void> {
    if (!this.toolbarCtrl || !this.store) return;
    const syncManager = this.opts.syncManager;
    if (!syncManager || !this.opts.storage.getSession()) {
      this.toolbarCtrl.setSyncState('none');
      return;
    }
    try {
      const level = this.store.level;
      const map = await syncManager.checkSyncStatuses([level]);
      const status = map.get(level.meta.id);
      this.toolbarCtrl.setSyncState(status?.state ?? 'local_only');
    } catch {
      // Network failure — don't change the sync state shown
    }
  }

  private async promptImport(): Promise<void> {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json,application/json';
    input.addEventListener('change', async () => {
      const file = input.files?.[0];
      if (!file) return;
      const text = await file.text();
      const res = parseImport(text);
      if (res.kind === 'error') { await showAlert(`Import failed: ${res.message}`); return; }
      if (res.kind === 'version-too-new') {
        await showAlert('This pack was made in a newer version. Please update the app.');
        return;
      }
      const existing = this.opts.storage.getUserLevels();
      const plan = planConflicts(res.levels, existing);
      for (const entry of plan) {
        if (!entry.conflict) { entry.resolution = 'replace'; continue; }
        const choice = await showChoice<Resolution>(
          'Level exists',
          `A level with id "${entry.incoming.meta.id}" already exists ("${entry.incoming.meta.name}"). What should we do?`,
          [
            { label: 'Replace', value: 'replace', primary: true },
            { label: 'Keep Both', value: 'keep-both' },
            { label: 'Skip', value: 'skip' },
          ],
        );
        if (!choice) return;
        entry.resolution = choice;
      }
      const updated = applyConflictPlan(plan, existing);
      for (const l of updated) {
        this.opts.storage.upsertUserLevel(l);
      }
      const imported = plan.filter((p) => p.resolution !== 'skip').length;
      await showAlert(`Imported ${imported} level(s).`);
    });
    input.click();
  }

  async openLevel(data: LevelData): Promise<void> {
    if (this.editorStorage.recoveryAvailable(data.meta.id)) {
      const recover = await showConfirm(
        'Unsaved changes',
        'An autosave from this level is newer than the saved copy. Recover it?',
        'Recover',
      );
      if (recover) {
        const recovered = this.editorStorage.recoverAutosave();
        if (recovered) data = recovered;
      } else {
        this.editorStorage.discardAutosave();
      }
    }
    this.loadLevel(data);
  }

  show(): void {
    this.root.style.display = 'flex';
    this.applyResponsive();
    this.resizeObserver = new ResizeObserver(() => this.applyResponsive());
    this.resizeObserver.observe(this.root);
    maybeShowTour(this.opts.storage);
    window.addEventListener('beforeunload', this.beforeUnloadHandler);
  }

  hide(): void {
    this.editorPreview?.destroy();
    this.editorPreview = null;
    this.root.style.display = 'none';
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    window.removeEventListener('beforeunload', this.beforeUnloadHandler);
  }

  private applyResponsive(): void {
    const compact = window.innerWidth < 768;
    this.root.classList.toggle('compact', compact);
    if (!compact) {
      this.outlineEl.classList.remove('open');
      this.inspectorEl.classList.remove('open');
    }
  }

  get outline(): HTMLDivElement { return this.outlineEl; }
  get preview(): HTMLDivElement { return this.previewEl; }
  get inspector(): HTMLDivElement { return this.inspectorEl; }
  get toolbar(): HTMLDivElement { return this.toolbarEl; }
  get fab(): HTMLDivElement { return this.fabEl; }
  get currentLevel(): LevelData | null { return this.store?.level ?? null; }
}

function triggerDefaults(kind: string): Record<string, unknown> {
  switch (kind) {
    case 'text': return { text: 'Hello', style: 'floating' };
    case 'color': return { baseColor: '#00ffff', accentColor: '#ff00ff' };
    case 'speed': return { speed: 1.5 };
    case 'camera': return { action: 'shake', intensity: 0.5 };
    case 'music': return { action: 'add_layer', layer: 'lead' };
    case 'gravity': return { direction: 'up', strength: 1.0 };
    case 'player_mode': return { playerMode: 'triangle' };
    case 'spawn': return {};
    case 'mode': return { targetMode: 'complex', transition: 'portal' };
    case 'fog': return { near: 30, far: 150, color: '#0a0a1a' };
    default: return {};
  }
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') || 'level';
}

async function offerDownload(filename: string, json: string): Promise<void> {
  const blob = new Blob([json], { type: 'application/json' });
  if ('share' in navigator && typeof navigator.share === 'function' && 'canShare' in navigator) {
    try {
      const file = new File([blob], filename, { type: 'application/json' });
      const canShareObj = (navigator as Navigator & { canShare: (d: { files: File[] }) => boolean });
      if (canShareObj.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: filename });
        return;
      }
    } catch { /* fall through to download */ }
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1000);
}
